import { AxiosBaseQueryFunction } from 'api/axiosBaseQuery';

import { StoreModel } from '@common/models/store.model';
import useFetchedEstimate from '@core/hooks/useFetchedEstimate';
import { StoresQueryParams } from '@ee-api/queryParams';
import { useFetchStoresQuery } from '@ee-api/stores-api/storesService';
import { useAppSelector } from '@redux/hooks';
import { selectZipcode } from '@redux/slices/zipcodeSlice';
import { createSelector } from '@reduxjs/toolkit';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { TypedUseQueryHookResult } from '@reduxjs/toolkit/dist/query/react';

const emptyArray = [] satisfies StoreModel[];

type useStoresReturn = TypedUseQueryHookResult<StoreModel[], StoresQueryParams, AxiosBaseQueryFunction> & {
    stores: StoreModel[];
    isFetchingStores: boolean;
    isErrorStores: boolean;
};

const selectWithDistance = createSelector(
    (data?: StoreModel[]) => data,
    (data) =>
        data
            ? data
                  .map((store) => ({
                      ...store,
                      distance: Math.round(store.distance * Math.pow(10, 1)) / Math.pow(10, 1),
                  }))
                  .sort((a, b) => a.distance - b.distance)
            : emptyArray
);

export const useStores = (): useStoresReturn => {
    const zipcode = useAppSelector(selectZipcode);
    const { fetchedEstimate } = useFetchedEstimate();

    const { stores, ...rest } = useFetchStoresQuery(
        zipcode ? { params: { zipCode: zipcode, storeId: fetchedEstimate?.storeId } } : skipToken,
        {
            selectFromResult: (result) => ({
                stores: selectWithDistance(result.isError ? emptyArray : result.data),
                ...result,
            }),
        }
    );

    return {
        stores,
        isFetchingStores: rest.isFetching || (!!zipcode && zipcode !== rest.originalArgs?.params?.zipCode),
        isErrorStores: rest.isError,
        ...rest,
    };
};
